import { Grid, Paper, Stack, Typography } from "@mui/material";
import group1 from "../../../assets/Images/dashboard/group1.png";
import { Metric } from "./data/Metric";
import { Constants } from "constants/constant";
import { formatEmissionValue } from "utilities/utilities";

interface ProjectMetricsProps {
  metrics: Metric[];
  isPortfolioView?: boolean; // Optional prop to indicate if it's in portfolio view
  currency:string; // Currency prop to display currency symbol
  designVariant?: "default" | "portfolio";
}

/**
 * ProjectMetrics component displays a grid of project metrics.
 *
 * @component
 * @param {ProjectMetricsProps} props - The component props.
 * @param {Metric[]} props.metrics - Array of metric data.
 * @returns {React.FC} The Project Metrics component.
 */
const ProjectMetrics: React.FC<ProjectMetricsProps> = ({
  metrics,
  isPortfolioView,
  currency,
  designVariant,
}) => {
  const UNIT_TCO2E=Constants.UNIT_TCO2E;
  const resolvedVariant = designVariant ?? (isPortfolioView ? "portfolio" : "default");
  const filteredMetrics = isPortfolioView
    ? metrics.filter(metric => metric.id !== 5)     // Hide only id: 5
    : metrics.filter(metric => metric.id !== 6);

    const updatedMetrics: Metric[] = filteredMetrics.map((metric) => {
    let updatedLabel = metric.label;

    // Special case for MAC unit format
    if (metric.key === 'mac') {
      updatedLabel = `Marginal Abatement Cost (${currency}/${UNIT_TCO2E})`;
    }
    // All other currency labels
    else if (metric.isCurrency) {
      updatedLabel = `${metric.label} (${currency})`;
    }

    return {
      ...metric,
      label: updatedLabel,
    };
  });

  /**
   * Generate a list of metric cards to be displayed.
   *
   * @returns {JSX.Element[]} Array of metric cards.
   */
  function getMetrics() {
    return updatedMetrics.map((s) => {
      const isPortfolioVariant = resolvedVariant === "portfolio";
      const isMacMetric = s.key === "mac";

      return (
        <Grid
          key={s.id}
          item
          sx={{
            boxShadow: isPortfolioVariant ? "0 1px 2px rgba(15, 23, 42, 0.06)" : 1,
            p: isPortfolioVariant ? 1.25 : 0.5,
            m: isPortfolioVariant ? 0.4 : 0.8,
            borderRadius: isPortfolioVariant ? "18px" : 5,
            border: isPortfolioVariant ? "1px solid #d7e0ea" : "none",
            backgroundImage: isPortfolioVariant ? "none" : `url(${group1})`,
            backgroundSize: isPortfolioVariant ? undefined : "cover",
            backgroundRepeat: isPortfolioVariant ? undefined : "no-repeat",
            backgroundPosition: isPortfolioVariant ? undefined : "center",
            backgroundColor: isPortfolioVariant ? "#fff" : undefined,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: isPortfolioVariant ? "84px" : "40px",
            height: isPortfolioVariant ? "84px" : undefined,
            width: isPortfolioVariant ? "calc((100% - 48px) / 6)" : "calc((100% - 160px) / 6)",
            maxWidth: isPortfolioVariant ? "calc((100% - 48px) / 6)" : "calc((100% - 160px) / 6)",
            flexBasis: isPortfolioVariant ? "calc((100% - 48px) / 6)" : "calc((100% - 160px) / 6)",
            flexGrow: 0,
            flexShrink: 0,
            fontSize: isPortfolioVariant ? "13px" : "14px",
          }}
        >
          <Stack direction={"column"} sx={{ flexGrow: 1 }}>
            <div
              style={{
                fontSize: isMacMetric ? "11px" : "12px",
                color: isPortfolioVariant ? "#7a8797" : undefined,
                whiteSpace: isMacMetric ? "nowrap" : undefined,
                letterSpacing: isMacMetric ? "-0.1px" : undefined,
              }}
            >
              {s.label}
            </div>
            
            <Typography
              sx={{
                fontSize: isPortfolioVariant ? "18px" : "20px",
                color: isPortfolioVariant ? "#0f2b52" : "inherit",
                mt: isPortfolioVariant ? 1 : 0,
              }}
              data-testid={s.value}
            >
              <b>
                {s.key === "averageAnnualRoi" && s.isAverageAnnualRoiPresent === false
                  ? "N/A"
                  : s.value != null
                  ? formatEmissionValue(s.value)
                  : "-"}
              </b>
            </Typography>
          </Stack>
          <Grid item alignSelf={"end"}>
            <img
              style={{
                maxWidth: isPortfolioVariant ? "68%" : "90%",
                maxHeight: isPortfolioVariant ? "68%" : "90%",
                opacity: isPortfolioVariant ? 0.45 : 1,
              }}
              src={s.src}
              alt={s.value + "Icon"}
            />
          </Grid>
        </Grid>
      );
    });
  }
  if (resolvedVariant === "portfolio") {
    return (
      <Grid item xs={12} sx={{ mb: 1.5, width: '100%', maxWidth: '100%', overflow: 'visible' }}>
        <Grid container spacing={0.5} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'visible' }}>
          {getMetrics()}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sx={{ mb: 0.5, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
        <Grid item sx={{ m: 1 }}>
          <div
            style={{
              fontFamily: "Inter",
              fontSize: "18px",
              fontWeight: "600",
              color: "#05004E",
              width: "100%",
              marginLeft: "8px",
            }}
          >
            Metrics
          </div>
        </Grid>
        <Grid container spacing={0.5} sx={{ padding: 0.5, pl: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
          {getMetrics()}
        </Grid>
      </Paper>
    </Grid>
  );
};

export default ProjectMetrics;
